import delay from '@redux-saga/delay-p';
import {
  call,
  cancel,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
  throttle,
} from 'typed-redux-saga/macro';

import { getInstance as getConfigServiceInstance } from 'Lib/ConfigService';
import { RegisteredLeverKeys, SESSION_LOCKED_LEVERS } from 'Lib/ConfigService/levers';
import { debugSession as debug } from 'Lib/debug';
import { getRehydrateLifecycle } from 'Lib/persist';
import type { Action, GetActionByType } from 'Lib/reduxType';
import { withErrorHandling } from 'Lib/sagaHelpers';
import getDeviceId, { isRecognizedDevice } from 'Lib/utils/deviceId';
import isNil from 'Lib/utils/isNil';
import { milliseconds as perfTimer } from 'Lib/utils/perfTimer';
import uuid from 'Lib/utils/uuid';

import { UPDATE_PROFILE_ACTIVE_PHOTO } from 'Store/attributes/constants';
import { START_BOOST } from 'Store/boost/constants';
import { STORE_SESSION } from 'Store/constants';
import { LOGIN_SUCCESS } from 'Store/mfa/constants';
import { RECS_DISLIKE, RECS_LIKE, RECS_REWIND, RECS_SUPERLIKE } from 'Store/recs/constants';
import { SET_ROUTE_INFO } from 'Store/routing/constants';
import { getIsUserLoggedIn } from 'Store/user/selectors';

import { trackEvent } from 'Store/sparks/actions';
import { AUTH_CHECK_DEVICE, SESSION_NEW } from 'Store/sparks/sparks.events';

import { COMMON_RESET_ACTION_TYPE } from '../commonActions';
import { appSessionStarted, sessionActivity, sessionTimeout, userSessionStarted } from './actions';
import {
  ADD_SESSION_ACTIVITY_TYPES,
  APP_SESSION_STARTED,
  SESSION_ACTIVITY,
  SESSION_ACTIVITY_THROTTLE_MS,
  SESSION_LEVERS_THROTTLE_MS,
  SESSION_METHOD_COLD_START,
  SESSION_METHOD_UNKNOWN,
  SESSION_TIMEOUT_MS,
  SESSION_TYPE_APP,
  SESSION_TYPE_USER,
  USER_SESSION_STARTED,
} from './constants';
import {
  getAppSessionId,
  getIsExpired,
  getTimeSinceLastActivity,
  getUserSessionId,
} from './selectors';

const DEFAULT_ACTIVITY_ACTION_TYPES = [
  SET_ROUTE_INFO,
  RECS_LIKE,
  RECS_DISLIKE,
  RECS_SUPERLIKE,
  RECS_REWIND,
  UPDATE_PROFILE_ACTIVE_PHOTO,
  START_BOOST,
];

const activityActionTypes = new Set(DEFAULT_ACTIVITY_ACTION_TYPES);
export function registerActivityActionType(actionTypes: string[]) {
  debug('new activity types', actionTypes);
  actionTypes.forEach(activityActionTypes.add, activityActionTypes);
}
export function resetActivityActionTypes() {
  activityActionTypes.clear();
  DEFAULT_ACTIVITY_ACTION_TYPES.forEach(activityActionTypes.add, activityActionTypes);
}

/**
 * Warm/Cold Start
 * ===============
 *
 * This is a native app concept that somewhat alien to the web, but for our purposes, we're
 * considering just the first session check to be "cold".
 *
 * We may consider changing this in the future.
 */
let isCold = true;

/**
 * Verifies that valid app and user sessions are open when they are required
 */
export function* checkSessionSaga(
  action?: Action,
  time: number = Date.now(),
  perfTime: number = perfTimer()
) {
  const isExpired = yield* select(getIsExpired);
  const sinceLastActivity = yield* select(getTimeSinceLastActivity, perfTime);
  const prevAppSessionId = yield* select(getAppSessionId);
  const prevUserSessionId = yield* select(getUserSessionId);
  const isLoggedIn = yield* select(getIsUserLoggedIn);

  const didSessionTimeout =
    isExpired || isNil(sinceLastActivity) || sinceLastActivity >= SESSION_TIMEOUT_MS;
  const sessionStartMethod = isCold ? SESSION_METHOD_COLD_START : SESSION_METHOD_UNKNOWN;

  if (didSessionTimeout || !prevAppSessionId) {
    const appSessionId = uuid();
    debug('new app session');
    yield* put(appSessionStarted(appSessionId, time, perfTime));
    yield* put(
      trackEvent(SESSION_NEW, {
        sessionStartMethod,
        sessionType: SESSION_TYPE_APP,
        appSessionId,
        appSessionTimeElapsed: 0,
        userSessionId: null,
        userSessionTimeElapsed: null,
      })
    );
  }

  if (isLoggedIn) {
    if (didSessionTimeout || !prevUserSessionId) {
      const userSessionId = uuid();
      debug('new user session');
      yield* put(userSessionStarted(userSessionId, time, perfTime));
      yield* put(
        trackEvent(SESSION_NEW, {
          sessionStartMethod,
          sessionType: SESSION_TYPE_USER,
          userSessionId,
          userSessionTimeElapsed: 0,
        })
      );
      yield* put(
        trackEvent(AUTH_CHECK_DEVICE, {
          persistentId: getDeviceId(),
          recognized: isRecognizedDevice(),
        })
      );
    }
  } else if (prevUserSessionId) {
    debug('end user session');
    yield* put(userSessionStarted(null, time, perfTime));
  }

  // Subsequent session checks are no longer considered "cold"
  isCold = false;
}

/**
 * Handles session-locked levers
 */
export function* sessionLeversSaga(
  sessionLockedLevers: Set<RegisteredLeverKeys> = SESSION_LOCKED_LEVERS
) {
  const configService = getConfigServiceInstance();

  if (sessionLockedLevers.size) {
    debug('lock session levers');
    let unlockFns = Array.from(sessionLockedLevers).map(
      (key) => configService.readLockLever(key)[1]
    );

    yield* throttle(
      SESSION_LEVERS_THROTTLE_MS,
      [APP_SESSION_STARTED, USER_SESSION_STARTED],
      // eslint-disable-next-line require-yield
      function* rereadLeversSaga() {
        debug('refresh session levers');
        unlockFns.forEach((unlockFn) => unlockFn());
        unlockFns = Array.from(sessionLockedLevers).map(
          (key) => configService.readLockLever(key)[1]
        );
      }
    );
  }
}

/**
 * Looks for any event that counts as session activity and performs a throttled update to the
 * last activity timestamp.
 */
export function* sessionActivitySaga() {
  yield* throttle(
    SESSION_ACTIVITY_THROTTLE_MS,
    Array.from(activityActionTypes),
    function* extendSessionSaga(action: Action) {
      debug('activity');
      yield* put(sessionActivity(Date.now(), action.type, perfTimer()));
    }
  );
}

/**
 * Handles the activity timeout by staging "races" that out pit session activity against a timeout.
 *
 * Once the "winner" is determined either by session activity or the tieout expiring, a new race
 * will start. Effectively, this means that activity will reset the timeout, and inactivity will
 * trigger a session check that will invalidate or turnover the session.
 */
export function* sessionTimeoutSaga() {
  while (true) {
    const { activity, timeout } = yield* race({
      activity: take(SESSION_ACTIVITY),
      timeout: delay(SESSION_TIMEOUT_MS),
    });
    if (activity) {
      yield* call(checkSessionSaga);
    } else if (timeout) {
      debug('session timeout');
      yield* put(sessionTimeout());
    }
  }
}

/**
 * Manages the activity and timeout sagas, restarting if new activity tasks are registered so that
 * sessionActivitySaga is aware of the new list of actions types
 */
export function* sessionRootInnerSaga() {
  const sessionActivityTask = yield* fork(sessionActivitySaga);
  const sessionTimeoutTask = yield* fork(sessionTimeoutSaga);

  const addSessionActivityActionType = yield* take(ADD_SESSION_ACTIVITY_TYPES);
  registerActivityActionType(
    (addSessionActivityActionType as GetActionByType<typeof ADD_SESSION_ACTIVITY_TYPES>).payload
      .actionTypes ?? []
  );

  yield* cancel(sessionActivityTask);
  yield* cancel(sessionTimeoutTask);

  yield* fork(sessionRootInnerSaga);
}

export const sessionRootSaga = withErrorHandling(
  function* _sessionRootSaga(action: Action, isInitialLoad: boolean = true) {
    debug('root saga');

    if (isInitialLoad) {
      yield* call(getRehydrateLifecycle(STORE_SESSION).block);

      // Initial session check
      yield* call(checkSessionSaga, action);
    }

    // Manage session levers
    yield* fork(sessionLeversSaga);

    // Subsequent events that impact session
    yield* takeEvery([...COMMON_RESET_ACTION_TYPE, LOGIN_SUCCESS], checkSessionSaga);

    yield* fork(sessionRootInnerSaga);
  },
  { name: 'sessionRootSaga' }
);
